import React, { useCallback, useEffect, useState } from "react";

import "./Sidebar.css";
import WelcomePanel from "./panels/welcomePanel/WelcomePanel";
import { IconButton } from "@deere/isg.component-library";
import { IconChevronDown, IconLegend } from "@deere/icons/new";

interface SidebarProps {
  toggleCabControl: (vehicleName?: string) => void;
  showCabControl: boolean;
  curJobsiteIcon: React.JSX.Element | undefined;
  setCurJobsiteIcon: (newIcon: React.JSX.Element | undefined) => void;
  showEngineeringTools: boolean;
  toggleEngineeringTools: () => void;
  orgName: string;
  setOrgName: (newOrgName: string) => void;
  fullMQTT: any;
  userLocation: any;
  setUserLocation: (loc: any) => void;
  isSpoofing: boolean;
  setIsSpoofing: (val: boolean) => void;
  originalLocation: any;
  setOriginalLocation: (loc: any) => void;
  mapGeometryList: any;
  currentTopicString: string;
  setGjson: (gjsonFile: any) => void;
  setCurJobsite: (newCurJobsite: string) => void;
  setShowAllVehicles: (setBoolean: boolean) => void;
  userName: any;
  totalEquipmentList: Object[] | undefined;
  OrgData: Object;
  curJobsite: string;
  orgDataSession: Object;
  orgDataSessionName: string;
  setOrgDataSessionName: (name: string) => void;
  vehicleIconClicked: boolean;
  setVehicleIconClicked: (clicked: boolean) => void;
  vehicleData: any;
  selectedFeatureId: string | null;
  setSelectedFeatureId: (id: string | null) => void;
  setMapGeometryList: (geometryList: any) => void;
  showGeometryOnMap: boolean;
  setShowGeometryOnMap: (value: boolean) => void;
  onJobsitePanelClose: () => void;
  setIsJobsiteUserSelected: (selected: boolean) => void;
  missionPreviewActive: boolean;
  setMissionPreviewActive: (active: boolean) => void;
  missionPreviewPaths: any[];
  setMissionPreviewPaths: (paths: any[]) => void;
  missionPreviewPoints: any[];
  setMissionPreviewPoints: (points: any[]) => void;
  followedVehicleId?: string | null;
  setFollowedVehicleId?: (id: string | null) => void;
  pendingNav?: { jobsite: string; session: string; equipmentId: string } | null;
  setPendingNav?: (nav: { jobsite: string; session: string; equipmentId: string } | null) => void;
}

function Sidebar({
  toggleCabControl,
  showCabControl,
  showEngineeringTools,
  toggleEngineeringTools,
  orgName,
  setOrgName,
  fullMQTT,
  userLocation,
  setUserLocation,
  isSpoofing,
  setIsSpoofing,
  originalLocation,
  setOriginalLocation,
  mapGeometryList,
  currentTopicString,
  setGjson,
  setCurJobsite,
  setIsJobsiteUserSelected,
  setShowAllVehicles,
  userName,
  totalEquipmentList,
  OrgData,
  curJobsite,
  orgDataSession,
  orgDataSessionName,
  setOrgDataSessionName,
  vehicleIconClicked,
  setVehicleIconClicked,
  vehicleData,
  selectedFeatureId,
  setSelectedFeatureId,
  setMapGeometryList,
  showGeometryOnMap,
  setShowGeometryOnMap,
  onJobsitePanelClose,
  missionPreviewActive,
  setMissionPreviewActive,
  missionPreviewPaths,
  setMissionPreviewPaths,
  missionPreviewPoints,
  setMissionPreviewPoints,
  followedVehicleId,
  setFollowedVehicleId,
  pendingNav,
  setPendingNav,
}: SidebarProps) {
  // Define state variables for zoom and translation
  const [isMini, setIsMini] = useState<boolean>(false);
  const [zoomPercent, setZoomPercent] = useState<number>(100);
  const [height, setHeight] = useState<string>("99%");

  // Draggable drawer states
  const [drawerHeight, setDrawerHeight] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startY, setStartY] = useState<number>(0);
  const [startHeight, setStartHeight] = useState<number>(0);
  const MIN_DRAWER_HEIGHT = 20; // Minimum height in pixels
  const DRAG_HANDLE_HEIGHT = 20; // Height of the drag handle

  // Function for backward compatibility - minimizes drawer on mobile
  const hideBar = useCallback(() => {
    if (isMini) {
      setDrawerHeight(MIN_DRAWER_HEIGHT);
    }
    // On desktop, do nothing (button can be hidden via CSS)
  }, [isMini, MIN_DRAWER_HEIGHT]);

  function getDvh() {
    const root = document.documentElement; // Get the root element
    const dvhValue = getComputedStyle(root).getPropertyValue("--dvh"); // Get the custom property
    return (parseFloat(dvhValue) * window.innerHeight) / 100; // Convert to pixels if needed
  }

  // Function to set scale and translate based on window width
  const setScaleTranslate = () => {
    const width = window.innerWidth;

    if (width < 420) {
      setIsMini(true);
      const newZoomPercent = (width / 420) * 100; // Calculate zoom percent

      // setZoomPercent(newZoomPercent);

      // Set translate based on zoom level
      const factor = newZoomPercent / 100; // Convert to scale factor

      setHeight((window.innerHeight / factor).toString()); // Example translation value

      // const newHeight = ((getDvh() - 120)).toString() + "px";
      // setHeight(newHeight);

      // Initialize drawer height for mobile
      if (drawerHeight === 0) {
        // setDrawerHeight(MIN_DRAWER_HEIGHT);
        setDrawerHeight(120);
      }
    } else if (width >= 420 && width <= 768) {
      setIsMini(true);
      // height: calc(100dvh - 20px);
      const newHeight = (getDvh() - 120).toString() + "px";
      setHeight(newHeight);

      // Initialize drawer height for tablet
      if (drawerHeight === 0) {
        // setDrawerHeight(MIN_DRAWER_HEIGHT);
        setDrawerHeight(120);
      }
    } else {
      setIsMini(false);
      setHeight("100%");
      setDrawerHeight(0); // Reset drawer height on desktop
    }
  };

  // Use effect to handle window resize
  useEffect(() => {
    setScaleTranslate(); // Set initial scale and translate on mount

    // Event listener for window resize
    window.addEventListener("resize", setScaleTranslate);
    return () => {
      // Clean up the event listener
      window.removeEventListener("resize", setScaleTranslate);
    };
  }, []);

  // Drag handlers for mobile drawer
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMini) return; // Only enable dragging on mobile

    setIsDragging(true);
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    setStartHeight(drawerHeight);

    // Prevent click event from firing if user is dragging
    e.preventDefault();
  };

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !isMini) return;

      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const deltaY = startY - clientY; // Positive delta means dragging up
      const newZoomPercent = (window.innerWidth / 420) * 100; // Calculate zoom percent
      let maxWindowHeight = window.innerHeight - (100 + 60);
      if (window.innerWidth < 420) {
        maxWindowHeight = (window.innerHeight - (100 + 60)) / (newZoomPercent / 100);
      }
      const newHeight = Math.max(
        MIN_DRAWER_HEIGHT,
        Math.min(startHeight + deltaY, maxWindowHeight),
      );

      setDrawerHeight(newHeight);

      // Prevent default to stop scrolling while dragging
      e.preventDefault();
    },
    [isDragging, startY, startHeight, isMini],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleHandleClick = (e: React.MouseEvent) => {
    if (!isMini) return;

    // Only toggle if this was a click, not a drag
    // Check if the mouse moved significantly during the interaction
    const wasDragging = Math.abs(drawerHeight - startHeight) > 10;
    if (wasDragging) return;

    // Toggle between minimized and full screen
    const currentHeight = drawerHeight;
    const halfScreenHeight = window.innerHeight / 2;

    if (currentHeight < halfScreenHeight) {
      // If currently minimized, expand to full screen
      // setDrawerHeight(window.innerHeight - 100 - DRAG_HANDLE_HEIGHT);
      // setDrawerHeight("100%");
      const newZoomPercent = (window.innerWidth / 420) * 100; // Calculate zoom percent
      if (window.innerWidth >= 420) {
        setDrawerHeight(window.innerHeight - (100 + 60));
      } else {
        setDrawerHeight((window.innerHeight - (100 + 60)) / (newZoomPercent / 100));
      }
    } else {
      // If currently expanded, minimize
      setDrawerHeight(MIN_DRAWER_HEIGHT);
    }
  };

  // Add event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const options = { passive: false };
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("touchmove", handleDragMove, options);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      const options = { passive: false };
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("touchmove", handleDragMove, options as any);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <>
      <div
        className="SideBar"
        style={{
          height: isMini ? `${drawerHeight}px` : height,
          bottom: isMini ? 0 : "auto",
          top: isMini ? "auto" : 0,
        }}
      >
        {isMini && (
          <div
            className="drag-handle"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onClick={handleHandleClick}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
          >
            <div className="drag-handle-bar"></div>
          </div>
        )}

        <WelcomePanel
          orgName={orgName}
          setOrgName={setOrgName}
          toggleCabControl={toggleCabControl}
          showCabControl={showCabControl}
          showEngineeringTools={showEngineeringTools}
          toggleEngineeringTools={toggleEngineeringTools}
          fullMQTT={fullMQTT}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
          isSpoofing={isSpoofing}
          setIsSpoofing={setIsSpoofing}
          originalLocation={originalLocation}
          setOriginalLocation={setOriginalLocation}
          mapGeometryList={mapGeometryList}
          currentTopicString={currentTopicString}
          userName={userName}
          setShowAllVehicles={setShowAllVehicles}
          totalEquipmentList={totalEquipmentList}
          setGjson={setGjson}
          setCurJobsite={setCurJobsite}
          setIsJobsiteUserSelected={setIsJobsiteUserSelected}
          hideBar={hideBar}
          OrgData={OrgData}
          curJobsite={curJobsite}
          orgDataSession={orgDataSession}
          orgDataSessionName={orgDataSessionName}
          setOrgDataSessionName={setOrgDataSessionName}
          vehicleIconClicked={vehicleIconClicked}
          vehicleData={vehicleData}
          setVehicleIconClicked={setVehicleIconClicked}
          setMapGeometryList={setMapGeometryList}
          selectedFeatureId={selectedFeatureId}
          setSelectedFeatureId={setSelectedFeatureId}
          showGeometryOnMap={showGeometryOnMap}
          setShowGeometryOnMap={setShowGeometryOnMap}
          handleJobsitePanelClose={onJobsitePanelClose}
          missionPreviewActive={missionPreviewActive}
          setMissionPreviewActive={setMissionPreviewActive}
          missionPreviewPaths={missionPreviewPaths}
          setMissionPreviewPaths={setMissionPreviewPaths}
          missionPreviewPoints={missionPreviewPoints}
          setMissionPreviewPoints={setMissionPreviewPoints}
          followedVehicleId={followedVehicleId}
          setFollowedVehicleId={setFollowedVehicleId}
          pendingNav={pendingNav}
          setPendingNav={setPendingNav}
        />
      </div>
    </>
  );
}

export default Sidebar;
